import React, { useState } from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import {
  Interests as InterestIcon,
  LocalFireDepartment as IntensityIcon,
  LocalOffer as TagIcon,
  Home as PreferencesIcon,
  Edit as EditIcon,
  Language as SourceIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';

import PreferenceUpdate from '../../../components/common/PreferenceUpdate';
import InterestsUpdate  from '../../../components/common/InterestsUpdate';
import IntentsUpdate    from '../../../components/common/IntentsUpdate';
import TagsUpdate       from '../../../components/common/TagsUpdate';
import AmenitiesUpdate  from '../../../components/common/AmenitiesUpdate';
import CreateNewTask    from '../../../components/common/CreateNewTask';
import AddNotes         from '../../../components/common/AddNotes';

// Modular sidebar components
import ContactDetails  from './components/ContactDetails';
import QuickActions    from './components/QuickActions';
import BudgetSection   from './components/BudgetSection';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  teal:      '#009688',
  tealLight: '#e0f2f1',
  border:    '#efefef',
  bg:        '#ffffff',
  primary:   '#1a1a2e',
  muted:     '#888',
  light:     '#bbb',
  radius:    '10px',
};

// ─── Empty placeholder ────────────────────────────────────────────────────────
const Empty = ({ text }) => (
  <Typography sx={{ fontSize: '0.7rem', color: T.light, fontStyle: 'italic' }}>
    {text}
  </Typography>
);

// ─── Chip list ────────────────────────────────────────────────────────────────
const ChipList = ({ items, color }) => {
  if (!items?.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
      {items.map((item, i) => {
        const lbl = typeof item === 'string' ? item : (item.name || item.label || item.value || '');
        return (
          <Chip
            key={i}
            label={lbl}
            size="small"
            sx={{
              height: 21, fontSize: '0.67rem', fontWeight: 500, borderRadius: '4px',
              bgcolor: `${color}18`, color, border: `1px solid ${color}35`,
              '& .MuiChip-label': { px: 0.9 },
            }}
          />
        );
      })}
    </Box>
  );
};

// ─── Section row ──────────────────────────────────────────────────────────────
const SectionRow = ({ icon, label, iconColor, onEdit, noBorder, children }) => (
  <Box sx={{ px: 2, py: 1.25, ...(!noBorder && { borderBottom: `1px solid ${T.border}` }) }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: children ? 0.75 : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        <Box sx={{ color: iconColor, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography sx={{ fontSize: '0.775rem', fontWeight: 600, color: T.primary, lineHeight: 1 }}>
          {label}
        </Typography>
      </Box>
      {onEdit && (
        <Tooltip title={`Edit ${label}`} placement="left">
          <IconButton size="small" onClick={onEdit}
            sx={{ width: 22, height: 22, color: T.teal, '&:hover': { bgcolor: T.tealLight } }}>
            <EditIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    {children}
  </Box>
);

// ─── Source row ───────────────────────────────────────────────────────────────
const SourceSection = ({ leadDetails }) => {
  const source = leadDetails?.source || leadDetails?.lead_source || '—';
  return (
    <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        <SourceIcon sx={{ fontSize: 15, color: '#5c6bc0' }} />
        <Typography sx={{ fontSize: '0.775rem', fontWeight: 600, color: T.primary }}>Source</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: T.teal, bgcolor: T.tealLight, px: 1, py: 0.2, borderRadius: '4px' }}>
        {source}
      </Typography>
    </Box>
  );
};

// ─── Right panel (Source + Prefs — budget lives on left sidebar) ──────────────
const InfoSidebar = ({ leadDetails, allPreferences, interestsData, tagsData, intentsData, amenitiesData, openDialog, editsDisabled }) => (
  <Box sx={{ borderRadius: T.radius, border: `1px solid ${T.border}`, bgcolor: T.bg, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
    <SourceSection leadDetails={leadDetails} />

    <SectionRow icon={<TuneIcon sx={{ fontSize: 15 }} />}       label="Preferences" iconColor="#3f51b5" onEdit={editsDisabled ? undefined : () => openDialog('preferences')}>
      {!allPreferences?.length ? <Empty text="No preferences specified" /> : <ChipList items={allPreferences} color="#3f51b5" />}
    </SectionRow>
    <SectionRow icon={<InterestIcon sx={{ fontSize: 15 }} />}   label="Interests"   iconColor="#e91e63" onEdit={editsDisabled ? undefined : () => openDialog('interests')}>
      {!interestsData?.length  ? <Empty text="No interests specified"   /> : <ChipList items={interestsData}  color="#e91e63" />}
    </SectionRow>
    <SectionRow icon={<TagIcon sx={{ fontSize: 15 }} />}        label="Tags"        iconColor="#009688" onEdit={editsDisabled ? undefined : () => openDialog('tags')}>
      {!tagsData?.length       ? <Empty text="No tags specified"        /> : <ChipList items={tagsData}       color="#009688" />}
    </SectionRow>
    <SectionRow icon={<IntensityIcon sx={{ fontSize: 15 }} />}  label="Intents"     iconColor="#ff9800" onEdit={editsDisabled ? undefined : () => openDialog('intents')}>
      {!intentsData?.length    ? <Empty text="No intents specified"     /> : <ChipList items={intentsData}    color="#ff9800" />}
    </SectionRow>
    <SectionRow icon={<PreferencesIcon sx={{ fontSize: 15 }} />} label="Amenities"  iconColor="#7e57c2" onEdit={editsDisabled ? undefined : () => openDialog('amenities')} noBorder>
      {!amenitiesData?.length  ? <Empty text="No amenities specified"   /> : <ChipList items={amenitiesData}  color="#7e57c2" />}
    </SectionRow>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
//
// variant="left"  → ContactDetails + Budget + QuickActions
// variant="right" → Source + Budget + Preferences panel
// ─────────────────────────────────────────────────────────────────────────────
const LeadSidebar = ({ leadId, leadsDefaultFilters, lead = null, variant = 'left' }) => {
  const { leadDetails, preferences, fetchLeadNotes } = useLeadDetails();

  const [updatedLeadData, setUpdatedLeadData] = useState(null);
  const [dialogState, setDialogState] = useState({
    preferences: false, interests: false, intents: false,
    tags: false, amenities: false,
  });
  const [openTaskModal, setOpenTaskModal] = useState(false);
  const [taskModalInitial, setTaskModalInitial] = useState(null);
  const [showAddNotes, setShowAddNotes] = useState(false);

  const openDialog  = (name) => setDialogState((p) => ({ ...p, [name]: true  }));
  const closeDialog = (name) => setDialogState((p) => ({ ...p, [name]: false }));

  const currentLead = updatedLeadData || leadDetails;
  const isLeadDeleted = Boolean(currentLead?.is_deleted);
  const isJunkStage = String(currentLead?.stage || '').toLowerCase().replace(/\s+/g, '_').trim() === 'junk';
  const sidebarActionsLocked = isLeadDeleted || isJunkStage;

  const { allPreferences, interestsData, intentsData, tagsData, amenitiesData, budgetData, contactInfo } =
    React.useMemo(() => {
      const saved = preferences?.saved_preferences || {};
      return {
        allPreferences: saved?.preferences || [],
        interestsData:  saved?.interests   || [],
        intentsData:    saved?.intents     || [],
        tagsData:       saved?.tags        || [],
        amenitiesData:  saved?.amenities   || [],
        budgetData:     saved?.budget      || { min: 0, max: 0 },
        contactInfo:    updatedLeadData?.contact_info || leadDetails?.contact_info || null,
      };
    }, [preferences, updatedLeadData, leadDetails]);

  // Shared dialogs rendered for both variants
  const Dialogs = () => (
    <>
      <PreferenceUpdate open={dialogState.preferences} onClose={() => closeDialog('preferences')} leadId={leadId} />
      <InterestsUpdate  open={dialogState.interests}   onClose={() => closeDialog('interests')}   leadId={leadId} />
      <TagsUpdate       open={dialogState.tags}         onClose={() => closeDialog('tags')}         leadId={leadId} leadTags={leadsDefaultFilters?.tags || []} />
      <IntentsUpdate    open={dialogState.intents}      onClose={() => closeDialog('intents')}      leadId={leadId} />
      <AmenitiesUpdate  open={dialogState.amenities}    onClose={() => closeDialog('amenities')}    leadId={leadId} />
      <AddNotes
        open={showAddNotes}
        onClose={() => setShowAddNotes(false)}
        leadIds={[currentLead?.id]}
        onComplete={() => {
          setShowAddNotes(false);
          if (leadId) fetchLeadNotes(leadId);
        }}
      />
      {openTaskModal && (
        <CreateNewTask
          open={openTaskModal}
          onClose={() => {
            setOpenTaskModal(false);
            setTaskModalInitial(null);
          }}
          leadId={leadId}
          lead={lead}
          source="lead"
          initialData={taskModalInitial || undefined}
          defaultItemType={taskModalInitial?.item_type || null}
        />
      )}
    </>
  );

  // ── LEFT variant ────────────────────────────────────────────────────────
  if (variant === 'left') {
    return (
      <Box sx={{ pb: 2 }}>
        <ContactDetails
          contactInfo={contactInfo}
          leadId={leadId}
          lead={currentLead}
          defaultFilters={leadsDefaultFilters}
          onLeadUpdate={(u) => setUpdatedLeadData(u)}
          openTaskModal={openTaskModal}
          setOpenTaskModal={setOpenTaskModal}
        />

        <BudgetSection budget={budgetData} leadId={leadId} readOnly={sidebarActionsLocked} />

        <QuickActions
          disabled={sidebarActionsLocked}
          onNewTask={() => {
            setTaskModalInitial(null);
            setOpenTaskModal(true);
          }}
          onAddNote={() => setShowAddNotes(true)}
          onLogSiteVisit={() => {
            const name =
              currentLead?.contact_info?.name ||
              currentLead?.contact?.name ||
              'Lead';
            setTaskModalInitial({
              item_type: 'site_visit',
              title: `Property Site Visit with ${name}`,
              description:
                'Property site visit to assess requirements and provide detailed consultation.',
            });
            setOpenTaskModal(true);
          }}
        />

        <Dialogs />
      </Box>
    );
  }

  // ── RIGHT variant ───────────────────────────────────────────────────────
  return (
    <Box sx={{ pb: 2 }}>
      <InfoSidebar
        leadDetails={currentLead}
        allPreferences={allPreferences}
        interestsData={interestsData}
        tagsData={tagsData}
        intentsData={intentsData}
        amenitiesData={amenitiesData}
        openDialog={openDialog}
        editsDisabled={sidebarActionsLocked}
      />
      <Dialogs />
    </Box>
  );
};

export default LeadSidebar;